// ./src/components/generic/BirthdayInput/BirthdayInput.tsx
//
// A birthday picker that copes with the common "no birth year" case. A toggle
// switches between:
//   - year known  → a full date picker (+ the person's current age shown beside it)
//   - year unknown → month + day selectors only
// Emits an ISO "YYYY-MM-DD" string (placeholder year 2000 when the year is
// unknown) plus the includesYear flag. Reusable for any contact/person form.

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Stack, Group, Checkbox, Input, Badge, Box } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";

import { useResponsiveDatePickerProps, SelectField } from "../form";
import { useTranslation } from "react-i18next";

import { DRAWER_COMBOBOX_PROPS } from "../mantineUiHelpers";

export interface BirthdayInputProps {
  /** ISO "YYYY-MM-DD" (year 2000 = placeholder when the year is unknown), or "". */
  value: string;
  /** Whether the year in `value` is real. */
  includesYear: boolean;
  /** Emits the next ISO value + includesYear flag. */
  onChange: (value: string, includesYear: boolean) => void;
  label?: string;
  error?: string;
  size?: "sm" | "md" | "lg";
  /** Mantine date dropdown mode (modal on phones). */
  dropdownType?: "modal" | "popover";
}

const PLACEHOLDER_YEAR = 2000;

const pad = (n: number): string => String(n).padStart(2, "0");

const parse = (v: string): { year: number | null; month: number | null; day: number | null } => {
  if (!v) {
    return { year: null, month: null, day: null };
  }

  const [y, m, d] = v.split("-").map((p) => Number(p));

  return {
    year: Number.isFinite(y) ? y : null,
    month: Number.isFinite(m) ? m : null,
    day: Number.isFinite(d) ? d : null,
  };
};

// Days in a month using a leap year (2000) so Feb allows 29 for year-less birthdays.
const daysInMonth = (month: number): number => new Date(PLACEHOLDER_YEAR, month, 0).getDate();

const computeAge = (year: number, month: number, day: number): number | null => {
  const today = new Date();
  let age = today.getFullYear() - year;
  const passed =
    today.getMonth() + 1 > month || (today.getMonth() + 1 === month && today.getDate() >= day);

  if (!passed) {
    age -= 1;
  }

  return age >= 0 && age < 200 ? age : null;
};

export const BirthdayInput: React.FC<BirthdayInputProps> = ({
  value,
  includesYear,
  onChange,
  label,
  error,
  size = "md",
  dropdownType,
}) => {
  const { t } = useTranslation(["contacts", "common"]);
  // Default to a centered modal on phones (via the shared hook); an explicit
  // `dropdownType` prop still wins.
  const responsiveDatePicker = useResponsiveDatePickerProps();
  const effectiveDropdownType = dropdownType ?? responsiveDatePicker.dropdownType;

  // Month/day are internal so a partial pick (month chosen, day not yet) isn't
  // lost when we emit "" upstream. `lastEmitted` lets us tell our own emits from
  // an external value change (form reset / record load) so we only resync then.
  const parsed = parse(value);
  const [month, setMonth] = useState<number | null>(parsed.month);
  const [day, setDay] = useState<number | null>(parsed.day);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      const next = parse(value);
      setMonth(next.month);
      setDay(next.day);
      lastEmitted.current = value;
    }
  }, [value]);

  const emit = useCallback(
    (m: number | null, d: number | null, incYear: boolean, year: number | null) => {
      if (m && d) {
        const y = incYear ? year ?? new Date().getFullYear() : PLACEHOLDER_YEAR;
        const next = `${y}-${pad(m)}-${pad(d)}`;
        lastEmitted.current = next;
        onChange(next, incYear);
      } else {
        lastEmitted.current = "";
        onChange("", incYear);
      }
    },
    [onChange]
  );

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_v, i) => ({
        value: String(i + 1),
        label: new Date(PLACEHOLDER_YEAR, i, 1).toLocaleString(undefined, { month: "long" }),
      })),
    []
  );

  const dayOptions = useMemo(() => {
    const max = month ? daysInMonth(month) : 31;

    return Array.from({ length: max }, (_v, i) => ({ value: String(i + 1), label: String(i + 1) }));
  }, [month]);

  const handleMonth = (next: string | null) => {
    const m = next ? Number(next) : null;
    // Clamp the day to the new month's length (e.g. 31 → 30/28).
    const d = m && day ? Math.min(day, daysInMonth(m)) : day;
    setMonth(m);
    setDay(d);
    emit(m, d, includesYear, parse(value).year);
  };

  const handleDay = (next: string | null) => {
    const d = next ? Number(next) : null;
    setDay(d);
    emit(month, d, includesYear, parse(value).year);
  };

  const handleFullDate = (next: string | null) => {
    if (!next) {
      setMonth(null);
      setDay(null);
      emit(null, null, true, null);

      return;
    }

    const p = parse(next);
    setMonth(p.month);
    setDay(p.day);
    emit(p.month, p.day, true, p.year);
  };

  const handleToggleYear = (checked: boolean) => {
    const year = checked ? parse(value).year ?? PLACEHOLDER_YEAR : PLACEHOLDER_YEAR;
    emit(month, day, checked, year);
  };

  // Age (year-known + a complete, real-year date).
  const age = useMemo(() => {
    if (!includesYear) {
      return null;
    }

    const p = parse(value);

    if (!p.year || p.year === PLACEHOLDER_YEAR || !p.month || !p.day) {
      return null;
    }

    return computeAge(p.year, p.month, p.day);
  }, [includesYear, value]);

  return (
    <Input.Wrapper label={label} error={error}>
      <Stack gap="xs" mt={4}>
        {includesYear ? (
          <Group gap="sm" align="center" wrap="nowrap">
            <Box style={{ flex: 1 }}>
              <DatePickerInput
                placeholder={t("contacts:form.fields.birthday.placeholder", { defaultValue: "Pick a date" })}
                value={value || null}
                onChange={handleFullDate}
                valueFormat="MMM DD, YYYY"
                dropdownType={effectiveDropdownType}
                modalProps={responsiveDatePicker.modalProps}
                clearable
                size={size}
              />
            </Box>
            {age !== null && (
              <Badge variant="light" color="gray" size="lg" style={{ whiteSpace: "nowrap" }}>
                {t("contacts:form.fields.birthday.age", { defaultValue: "Age {{age}}", age })}
              </Badge>
            )}
          </Group>
        ) : (
          <Group grow align="flex-start">
            <SelectField
              placeholder={t("contacts:form.fields.birthday.month", { defaultValue: "Month" })}
              data={monthOptions}
              value={month ? String(month) : null}
              onChange={(_name, v) => handleMonth(v)}
              clearable
              comboboxProps={DRAWER_COMBOBOX_PROPS}
              size={size}
            />
            <SelectField
              placeholder={t("contacts:form.fields.birthday.day", { defaultValue: "Day" })}
              data={dayOptions}
              value={day ? String(day) : null}
              onChange={(_name, v) => handleDay(v)}
              clearable
              disabled={!month}
              comboboxProps={DRAWER_COMBOBOX_PROPS}
              size={size}
            />
          </Group>
        )}

        <Checkbox
          label={t("contacts:form.fields.birthdayIncludesYear.label", { defaultValue: "The birth year is known" })}
          checked={includesYear}
          onChange={(e) => handleToggleYear(e.currentTarget.checked)}
          size="md"
        />
      </Stack>
    </Input.Wrapper>
  );
};
